import React, { useState } from "react";
import PublicPageLayout from "@/components/PublicPageLayout";
import Seo, { pageSchema } from "@/components/Seo";
import { WhatsAppButton } from "@/components/WhatsAppFab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SITE, WA_MESSAGES, waLink } from "@/lib/site";
import { Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const path = "/contact";
  const title = "Contact / Get Quote | Registry Feed";
  const description = "Talk to Registry Feed about the MCA & GST company database. WhatsApp us for instant pricing, or leave your details and we'll get back within one business day.";
  const bc = [{ name: "Home", path: "/" }, { name: "Contact", path }];
  const jsonLd = pageSchema({ title, description, path, breadcrumbs: bc });
  const [busy, setBusy] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const name = form.get("name") || "";
    const company = form.get("company") || "";
    const email = form.get("email") || "";
    const need = form.get("need") || "MCA/GST database";
    const message = form.get("message") || "";
    // Since we don't want to touch backend APIs, funnel the form into WhatsApp with a pre-filled quote request.
    const composed = `Hi Registry Feed, I want a quote.\n\nName: ${name}\nCompany: ${company}\nEmail: ${email}\nRequirement: ${need}\nMessage: ${message}`;
    setBusy(true);
    window.open(waLink(composed), "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp with your details pre-filled");
    setBusy(false);
  };

  return (
    <PublicPageLayout breadcrumbs={bc}>
      <Seo title={title} description={description} path={path} jsonLd={jsonLd} />
      <section className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14 sm:py-20 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Talk to sales.</h1>
          <p className="mt-4 text-muted-foreground">WhatsApp is the fastest — we usually reply within a few hours during business days.</p>
          <div className="mt-8 space-y-4">
            <a href={`mailto:${SITE.salesEmail}`} className="flex items-center gap-3 rounded-lg border border-border bg-card p-4 hover:border-primary/50">
              <Mail className="h-5 w-5 text-primary" />
              <div>
                <div className="text-xs uppercase text-muted-foreground tracking-widest">Email</div>
                <div className="text-sm font-medium">{SITE.salesEmail}</div>
              </div>
            </a>
            <a href={waLink(WA_MESSAGES.general)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-lg border border-border bg-card p-4 hover:border-primary/50">
              <MessageCircle className="h-5 w-5 text-[#25D366]" />
              <div>
                <div className="text-xs uppercase text-muted-foreground tracking-widest">WhatsApp</div>
                <div className="text-sm font-medium num">{SITE.whatsappDisplay}</div>
              </div>
            </a>
          </div>
        </div>

        <form onSubmit={onSubmit} className="lg:col-span-3 rounded-xl border border-border bg-card p-6 space-y-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label htmlFor="c-name">Full name</Label><Input id="c-name" name="name" required data-testid="contact-name" /></div>
            <div><Label htmlFor="c-company">Company</Label><Input id="c-company" name="company" required data-testid="contact-company" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label htmlFor="c-email">Work email</Label><Input id="c-email" name="email" type="email" required data-testid="contact-email" /></div>
            <div><Label htmlFor="c-need">What you need</Label><Input id="c-need" name="need" placeholder="e.g. GST database, Maharashtra state" data-testid="contact-need" /></div>
          </div>
          <div><Label htmlFor="c-msg">Message (optional)</Label><Textarea id="c-msg" name="message" rows={4} data-testid="contact-message" /></div>
          <div className="flex flex-wrap gap-3 pt-2">
            <Button type="submit" disabled={busy} data-testid="contact-submit">Send via WhatsApp</Button>
            <WhatsAppButton message={WA_MESSAGES.general} label="Quick WhatsApp" />
          </div>
          <p className="text-xs text-muted-foreground">Submitting opens WhatsApp with your details pre-filled — you review and hit send. Nothing is sent without your click.</p>
        </form>
      </section>
    </PublicPageLayout>
  );
}
